import { Button, Space, Typography } from "antd";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import type { VolunteerSubject, VolunteerTask } from "../types";
import type { VolunteerStore } from "../store";
import { SortableTaskItem } from "./SortableTaskItem";
import { SubjectFormModal } from "./SubjectFormModal";
import { StackRow, wrapText } from "./responsive";

interface Props {
  store: VolunteerStore;
  subject: VolunteerSubject | null;
  tasks: VolunteerTask[];
  isAdmin: boolean;
  dragHandleProps?: {
    attributes: React.HTMLAttributes<HTMLDivElement>;
    listeners: ReturnType<typeof useSortable>["listeners"];
  };
}

export const SubjectGroup = observer(
  ({ store, subject, tasks, isAdmin, dragHandleProps }: Props) => {
    const [editOpen, setEditOpen] = useState(false);
    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = tasks.map((t) => t.objectId!);
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;
      store.reorderTasks(arrayMove(ids, oldIndex, newIndex));
    };

    return (
      <div style={{ marginBottom: 32 }}>
        <StackRow $gap={8} style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            {dragHandleProps && isAdmin && (
              <div
                {...dragHandleProps.attributes}
                {...dragHandleProps.listeners}
                style={{
                  cursor: "grab",
                  touchAction: "none",
                  color: "rgba(0, 0, 0, 0.45)",
                  userSelect: "none",
                  flexShrink: 0,
                }}
              >
                ⠿
              </div>
            )}
            <Typography.Title level={3} style={{ margin: 0, flex: 1, minWidth: 0, ...wrapText }}>
              {subject?.title ?? "Ohne Thema"}
            </Typography.Title>
          </div>
          {isAdmin && subject && (
            <Space>
              <Button size="small" onClick={() => setEditOpen(true)}>
                Thema bearbeiten
              </Button>
            </Space>
          )}
        </StackRow>

        {isAdmin ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={tasks.map((t) => t.objectId!)}
              strategy={verticalListSortingStrategy}
            >
              {tasks.map((task) => (
                <SortableTaskItem key={task.objectId} store={store} task={task} isAdmin={isAdmin} />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          tasks.map((task) => (
            <SortableTaskItem key={task.objectId} store={store} task={task} isAdmin={isAdmin} />
          ))
        )}

        {isAdmin && subject && (
          <SubjectFormModal store={store} subject={subject} open={editOpen} onClose={() => setEditOpen(false)} />
        )}
      </div>
    );
  },
);
